import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { AssetPerson } from '../../models/employee-asset.model';
import { OffboardingAssignment, OffboardingAssigneeContext } from '../../models/offboarding.model';
import { OffboardingService } from '../../services/offboarding.service';
import { assetErrorKey, assetOperationId } from '../common/employee-assets/asset.helpers';
import { ASSET } from '../common/employee-assets/asset.constants';

const EDITOR = {empty: '', separator: '-', reason: 'offboarding.assignment.automatic'};
@Component({selector: 'app-offboarding-assignment-editor', templateUrl: './offboarding-assignment-editor.component.html',
  styleUrls: ['./offboarding-sequential.component.css', './offboarding-workspace-content.css']})
export class OffboardingAssignmentEditorComponent implements OnInit, OnDestroy {
  @Input() item: OffboardingAssignment;
  @Input() caseId: string;
  @Input() disabled = false;
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() busyChange = new EventEmitter<boolean>();
  draft: OffboardingAssignment;
  context: OffboardingAssigneeContext;
  primaryName = EDITOR.empty;
  substituteName = EDITOR.empty;
  busy = false;
  explicitSubstitute = false;
  automatic = false;
  errorKey = EDITOR.empty;
  assetConstants = ASSET;
  currentPair: OffboardingAssignment;
  fieldPrefix = EDITOR.empty;
  private submittedContent = EDITOR.empty;
  private destroyed = new Subject<void>();
  constructor(private api: OffboardingService) {}
  ngOnInit(): void {
    this.draft = Object.assign({}, this.item, {operationId: assetOperationId(), expectedVersion: this.item.version, caseId: this.caseId});
    if (!this.caseId) { this.draft.substituteId = undefined; }
    const unassigned = !this.item.active || !this.item.assigneeId;
    this.automatic = !this.caseId && unassigned;
    if (unassigned) { this.draft.assigneeId = EDITOR.empty; this.draft.substituteId = EDITOR.empty; }
    this.primaryName = this.item.assigneeName || EDITOR.empty;
    this.substituteName = this.draft.substituteId ? this.item.substituteName || EDITOR.empty : EDITOR.empty;
    this.explicitSubstitute = !!this.draft.substituteId;
    this.context = this.caseId ? {caseId: this.caseId, assignmentId: this.item.id} : {successors: true};
    this.fieldPrefix = this.item.function + EDITOR.separator + this.item.locationCode;
  }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }
  get canSave(): boolean {
    return !this.busy && !this.disabled && (!this.automatic || !this.caseId) && (this.automatic || !!this.draft.assigneeId)
      && (!this.explicitSubstitute || !!this.draft.substituteId) && (this.automatic || this.draft.assigneeId !== this.draft.substituteId);
  }
  modeChanged(): void {
    this.errorKey = EDITOR.empty;
    if (this.automatic) { this.draft.assigneeId = EDITOR.empty; this.draft.substituteId = undefined; this.explicitSubstitute = false; }
  }
  substituteChanged(): void {
    if (!this.explicitSubstitute) { this.draft.substituteId = undefined; this.substituteName = EDITOR.empty; }
  }
  choosePrimary(person: AssetPerson): void { this.draft.assigneeId = person.id; this.primaryName = person.name; }
  chooseSubstitute(person: AssetPerson): void { this.draft.substituteId = person.id; this.substituteName = person.name; }
  submit(): void {
    if (!this.canSave || this.errorKey === ASSET.conflictKey || this.errorKey === ASSET.deniedKey) { return; }
    this.prepareOperation(); this.setBusy(true); this.errorKey = EDITOR.empty;
    const request = this.item.id ? this.api.replace(this.item.id, this.draft) : this.api.assign(this.draft);
    request.takeUntil(this.destroyed).subscribe(() => { this.setBusy(false); this.saved.emit(); }, error => {
      this.setBusy(false); this.errorKey = assetErrorKey(error, true);
    });
  }
  checkCurrent(): void {
    if (this.busy || !this.item.id) { return; }
    this.setBusy(true);
    if (this.caseId) {
      this.api.get(this.caseId).takeUntil(this.destroyed).subscribe(result => {
        const obligation = (result.replacementObligations || []).filter(row => row.assignmentId === this.item.id)[ASSET.zero];
        if (!obligation || !obligation.canReplace) { this.setBusy(false); this.errorKey = ASSET.deniedKey; return; }
        this.checked({version: obligation.currentVersion, function: obligation.function,
          assigneeId: obligation.currentAssigneeId, assigneeName: obligation.currentAssigneeName,
          substituteId: obligation.currentSubstituteId, substituteName: obligation.currentSubstituteName});
      }, error => { this.setBusy(false); this.errorKey = assetErrorKey(error, false); });
      return;
    }
    this.api.assignments(this.item.locationCode).takeUntil(this.destroyed).subscribe(result => {
      this.checked(result.filter(row => row.id === this.item.id)[ASSET.zero]);
    }, error => { this.setBusy(false); this.errorKey = assetErrorKey(error, false); });
  }
  private checked(pair: OffboardingAssignment): void {
    this.setBusy(false);
    if (!pair || pair.waitReason) { this.errorKey = ASSET.deniedKey; return; }
    this.currentPair = pair; this.draft.expectedVersion = pair.version; this.draft.operationId = assetOperationId();
    this.submittedContent = EDITOR.empty; this.errorKey = EDITOR.empty;
  }
  private prepareOperation(): void {
    const content = JSON.stringify([this.draft.assigneeId, this.draft.substituteId, this.draft.locationCode, this.draft.expectedVersion]);
    if (this.submittedContent && this.submittedContent !== content) { this.draft.operationId = assetOperationId(); }
    this.submittedContent = content;
  }
  private setBusy(value: boolean): void { this.busy = value; this.busyChange.emit(value); }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-assignment-editor.component.ts